<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { ContentBlock, type TContentBlockVariant } from '~/components/ui/content-block'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Textarea } from '~/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import type { FormServerErrorResponse, ILocation, IResponseData } from '~/types'
import { useApiPost } from '~/composables/useApiPost'
import type { IOrganization } from '~/components/entity/organization/types'
import type { ICreditPurpose } from './types'
import type { ICredit } from '~/components/entity/credit/item'
import { WidgetPhoneInput } from '~/components/widget/phone-input'
import { FeatureCustomConsent } from '~/components/feature/custom-consent'
import { WidgetInnInput } from '~/components/widget/inn-input'
import { Checkbox } from '~/components/ui/checkbox'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import { Label } from '~/components/ui/label'
import { ANCHOR_IDS } from '~/lib/constants'
import type { THeading } from '~/components/ui/heading'
import { useToast } from '~/components/ui/toast'

interface ILeadSubmissionData extends Partial<Record<string, string | number | null>> {
  leadable_type: 'credit'
  leadable_id?: number | string | null
  organization_type?: string
  organization_id?: number
  currency_id: number
  last_name: string
  first_name: string
  middle_name?: string
  inn?: string
  phone: string
  email: string
  amount: number
  period: string
  location_id: number
  branch_id?: number
  product_id?: number
  credit_purpose_id?: number
  consent_version?: string
}

const config = useRuntimeConfig()
const { t } = useI18n()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldLastName: useValidationMessage('requiredFieldLastName'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue50: useValidationMessage('maxValue', 50),
  requiredFieldFirstName: useValidationMessage('requiredFieldFirstName'),
  requiredEmailInvalid: useValidationMessage('requiredEmailInvalid'),
  chooseCurrency: useValidationMessage('chooseCurrency'),
  requiredCreditAmount: useValidationMessage('requiredCreditAmount'),
  chooseCreditLength: useValidationMessage('chooseCreditLength'),
  requiredCity: useValidationMessage('requiredCity'),
  requiredBranch: useValidationMessage('requiredBranch'),
  requiredProduct: useValidationMessage('requiredCredit'),
  requiredPurpose: useValidationMessage('requiredPurpose'),
  requiredInn: useValidationMessage('requiredInn'),
  agreement: useValidationMessage('agreement'),

}

const apiParams = {
  per_page: 100
}

const props = defineProps<{
  organization?: IOrganization
  credit?: ICredit
  headingTag?: THeading
  headingClass?: string
  headingText?: string
  variant?: TContentBlockVariant
  availableCurrencies?: { id: number; code: string }[]
  disableAnchor?: boolean
}>()

// Data fetching
const { data } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', apiParams),
  { key: 'locations' }
)
const cities = computed(() => data.value?.data)

// Organization branches (only when branches are enabled)
const branchesApiParams = computed(() => {
  if (!props.organization || !props.organization.permissions.credit_request_settings?.with_branches)
    return null
  return {
    'filter[organization_id]': props.organization.id,
    'filter[organization_type]': props.organization.organization_type
  }
})
const { data: branches_data } = useApi<IResponseData<ILocation[]>>(
  () => (branchesApiParams.value ? useUrlWithParams('/branches', branchesApiParams.value) : null),
  {
    key: 'branches',
    immediate: !!branchesApiParams.value
  }
)
const branches = computed(() => branches_data.value?.data)

// Products (only when products are enabled)
const isProductsEnabled = computed(() => {
  return Boolean(
    props.organization && props.organization.permissions.credit_request_settings?.with_products
  )
})

const productsApiParams = computed(() => {
  if (!isProductsEnabled.value) return null

  return {
    per_page: '1000',
    'filter[organization_id]': props.organization.id,
    'filter[organization_type]': props.organization.organization_type
  }
})

const { data: products_data } = useApi<IResponseData<ICredit[]>>(
  () => useUrlWithParams('/credits', productsApiParams.value!),
  {
    key: `products-${props.organization?.id}`,
    enabled: isProductsEnabled
  }
)

const products = computed(() => products_data.value?.data)

// Credit purposes (only for general credit forms without organization)
const creditPurposesEnabled = computed(
  () => !props.organization && !props.credit?.purposes
)

const { data: creditPurposeData } = useApi<IResponseData<ICreditPurpose[]>>(
  () => '/credit_purposes',
  {
    key: 'credit_purposes',
    immediate: creditPurposesEnabled.value,
    server: creditPurposesEnabled.value
  }
)

const creditPurposes = computed(() => {
  return props.credit?.purposes ?? creditPurposeData.value?.data ?? []
})

// Currencies
const currencies = computed(() => {
  if (props.availableCurrencies) return props.availableCurrencies
  if (props.credit?.currencies) return props.credit.currencies
  return [
    { id: 1, code: 'KGS' },
    { id: 2, code: 'USD' },
    { id: 3, code: 'EUR' }
  ]
})

// Form configuration
const periods = computed(() => [
  { value: '3', title: t('form.credit_request.periods.months_3') },
  { value: '6', title: t('form.credit_request.periods.months_6') },
  { value: '12', title: t('form.credit_request.periods.months_12') },
  { value: '18', title: t('form.credit_request.periods.months_18') },
  { value: '24', title: t('form.credit_request.periods.months_24') },
  { value: '36', title: t('form.credit_request.periods.months_36') },
  { value: '48', title: t('form.credit_request.periods.months_48') },
  { value: '60', title: t('form.credit_request.periods.months_60') }
])

// Computed flags for different form features
const isOrganizationForm = computed(() => !!props.organization)

const showBranchesField = computed(() => {
  return props.organization?.permissions.credit_request_settings?.with_branches === true
})

const showProductsField = computed(() => {
  return props.organization?.permissions.credit_request_settings?.with_products === true
})

const showCreditPurposesField = computed(() => !isOrganizationForm.value)

// Organization may arrive either directly or through the selected product
const leadOrganization = computed(() => props.organization ?? props.credit?.organization)

const isInnRequired = computed(() => leadOrganization.value?.permissions?.is_inn_required === true)

// Bank-specific consent replaces our own agreement when the detail response provides it
const customConsent = computed(() => props.credit?.custom_consent ?? props.organization?.custom_consent ?? null)

// Form schema
const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      last_name: z
        .string({ message: validationMessages.requiredFieldLastName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      first_name: z
        .string({ message: validationMessages.requiredFieldFirstName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue50 }),
      middle_name: z.string().optional(),
      inn: useInnValidationSchema(isInnRequired.value, validationMessages.requiredInn),
      phone: usePhoneValidationSchema(),
      email: z
        .string()
        .email({ message: validationMessages.requiredEmailInvalid })
        .optional()
        .or(z.literal('')),
      currency_id: z.number().min(1, { message: validationMessages.chooseCurrency }),
      amount: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z
          .number({
            invalid_type_error: validationMessages.requiredCreditAmount,
            required_error: validationMessages.requiredCreditAmount
          })
          .min(1, { message: validationMessages.requiredCreditAmount })
      ),
      period: z.string().min(1, { message: validationMessages.chooseCreditLength }),
      location_id: z.number().min(1, { message: validationMessages.requiredCity }),
      branch_id: z.number().optional(),
      product_id: showProductsField.value
        ? z.number().min(1, { message: validationMessages.requiredProduct })
        : z.number().optional(),
      credit_purpose_id: showCreditPurposesField.value
        ? z.number().min(1, { message: validationMessages.requiredPurpose })
        : z.number().optional(),
      agreement: z
        .boolean({ required_error: validationMessages.agreement })
        .refine((val) => val, { message: validationMessages.agreement }),
      comments: z.string().optional()
    })
  )
)

// Get route for query params
const route = useRoute()

// Helper function to find currency ID by code
const getCurrencyIdByCode = (code: string | undefined): number | undefined => {
  if (!code) return undefined
  return currencies.value.find((c) => c.code.toLowerCase() === code.toLowerCase())?.id
}

// Form initialization
const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    location_id: cities.value?.[0]?.id ?? undefined,
    branch_id: showBranchesField.value ? branches.value?.[0]?.id : undefined,
    product_id: showProductsField.value ? products.value?.[0]?.id : undefined,
    credit_purpose_id:
      (route.query.purpose ? Number(route.query.purpose) : undefined) ??
      (showCreditPurposesField.value ? creditPurposes.value?.[0]?.id : undefined),
    currency_id:
      getCurrencyIdByCode(route.query.currency as string) ?? currencies.value[0]?.id ?? undefined,
    amount: route.query.amount ? Number(route.query.amount) : undefined,
    period: (route.query.period as string) ?? periods.value[0].value,
    agreement: false
  }
})

// Watchers to update form values when data loads
watch(cities, (newCities) => {
  if (newCities?.length) {
    form.setFieldValue('location_id', newCities[0].id)
  }
})

watch(branches, (newBranches) => {
  if (newBranches?.length && showBranchesField.value) {
    form.setFieldValue('branch_id', newBranches[0].id)
  }
})

watch(products, (newProducts) => {
  if (newProducts?.length && showProductsField.value) {
    form.setFieldValue('product_id', newProducts[0].id)
  }
})

watch(creditPurposes, (newPurposes) => {
  if (newPurposes?.length && showCreditPurposesField.value) {
    form.setFieldValue('credit_purpose_id', newPurposes[0].id)
  }
})

// Branches filtered by selected city/region
const filteredBranches = computed(() => {
  const selectedLocationId = form.values.location_id
  if (!selectedLocationId) return branches.value ?? []
  return (branches.value ?? []).filter((branch) => branch.location_id === selectedLocationId)
})

const hasBranchesInLocation = computed(() => filteredBranches.value.length > 0)

watch(filteredBranches, (newBranches) => {
  if (!showBranchesField.value) return
  if (newBranches.length === 0) {
    form.setFieldValue('branch_id', undefined)
    form.setFieldError('branch_id', undefined)
    return
  }
  const currentBranchId = form.values.branch_id
  const isCurrentValid = newBranches.some((branch) => branch.id === currentBranchId)
  if (!isCurrentValid) {
    form.setFieldValue('branch_id', newBranches[0]?.id)
  }
})

// Form submission state
const { alert, requestId, formBlock, handleSuccessSubmit } = useFormRequestSuccess()
const { trackEvent } = useAnalytics()
const { toast } = useToast()

// Error handling
const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors
    const message = serverError._data?.message

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    } else if (message) {
      toast({
        title: message,
        variant: 'destructive'
      })
    }
  }
}

// Form submission
const onSubmit = form.handleSubmit(async (values) => {
  if (showBranchesField.value && hasBranchesInLocation.value && !values.branch_id) {
    form.setFieldError('branch_id', validationMessages.requiredBranch)
    return
  }

  const leadableId = values.product_id || props.credit?.id || null

  const submitData: ILeadSubmissionData = {
    leadable_type: 'credit',
    leadable_id: leadableId,
    currency_id: values.currency_id,
    last_name: values.last_name,
    first_name: values.first_name,
    middle_name: values.middle_name || '',
    phone: `+996${values.phone}`,
    email: values.email,
    amount: values.amount,
    period: values.period,
    location_id: values.location_id,
    comments: values.comments || '',
    fingerprint_id: useFingerprintStore().visitorId
  }

  if (isInnRequired.value && values.inn) {
    submitData.inn = values.inn
  }

  // Add organization data if this is an organization form
  if (props.organization) {
    submitData.organization_type = props.organization.organization_type
    submitData.organization_id = props.organization.id
  }

  // Add credit-specific data
  if (props.credit?.organization_id) {
    submitData.organization_id = props.credit.organization_id
  }
  if (props.credit?.organization_type) {
    submitData.organization_type = props.credit.organization_type
  }

  // Add optional fields
  if (showBranchesField.value && hasBranchesInLocation.value && values.branch_id) {
    submitData.branch_id = values.branch_id
  }

  if (showCreditPurposesField.value && values.credit_purpose_id) {
    submitData.credit_purpose_id = values.credit_purpose_id
  }

  if (customConsent.value) {
    submitData.consent_version = customConsent.value.version
  }

  const { data, error } = await useApiPost<IResponseData<{ id: string }>>('/leads', submitData)

  if (error) {
    const errorFields = [
      'last_name',
      'first_name',
      'middle_name',
      'phone',
      'email',
      'currency_id',
      'amount',
      'period',
      'agreement',
      'location_id'
    ]

    if (isInnRequired.value) errorFields.push('inn')
    if (showBranchesField.value) errorFields.push('branch_id')
    if (showProductsField.value) errorFields.push('product_id')
    if (showCreditPurposesField.value) errorFields.push('credit_purpose_id')

    handleServerValidationErrors(error, form.setFieldError, errorFields)
    trackEvent('lead_form_error', { lead_type: 'credit' })
  } else {
    trackEvent('generate_lead', {
      lead_type: 'credit',
      product_id: leadableId,
      organization_id: submitData.organization_id,
      organization_type: submitData.organization_type
    })
    await handleSuccessSubmit(data?.data.id ?? '')
  }
})
</script>

<template>
  <ContentBlock
    :id="disableAnchor ? undefined : ANCHOR_IDS.FEATURE_CREDIT_FORM_REQUEST"
    ref="formBlock"
    :heading-text="props.headingText"
    :show-link-icon="!disableAnchor"
    :heading-tag="props.headingTag"
    :heading-class="props.headingClass"
    :variant="props.variant"
    class="FormScrollOffset"
  >
    <Alert v-if="alert" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertTitle>{{ $t('form.credit_request.alert.title') }}</AlertTitle>
      <AlertDescription>
        <div>
          {{ $t('form.credit_request.alert.description') }}
        </div>
        <div>{{ $t('form.credit_request.alert.request_id') }} #{{ requestId }}.</div>
      </AlertDescription>
    </Alert>
    <div v-else class="space-y-5">
      <p>
        {{ $t('form.credit_request.intro') }}
      </p>
      <form class="flex flex-col gap-5" @submit="onSubmit">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField v-slot="{ componentField, errors }" name="last_name">
            <FormItem>
              <FormLabel>{{ $t('common.last_name') }}*</FormLabel>
              <FormControl>
                <Input type="text" :placeholder="$t('common.last_name')" v-bind="componentField" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="first_name">
            <FormItem>
              <FormLabel>{{ $t('common.first_name') }}*</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.first_name')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField }" name="middle_name">
            <FormItem>
              <FormLabel>{{ $t('common.middle_name') }}</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.middle_name')" />
              </FormControl>
            </FormItem>
          </FormField>

          <FormField v-if="isInnRequired" v-slot="{ componentField, errors }" name="inn">
            <FormItem>
              <FormLabel>{{ $t('common.inn') }}*</FormLabel>
              <FormControl>
                <WidgetInnInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="phone">
            <FormItem>
              <FormLabel>{{ $t('common.phone') }}*</FormLabel>
              <FormControl>
                <WidgetPhoneInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="email">
            <FormItem>
              <FormLabel>{{ $t('common.email') }}</FormLabel>
              <FormControl>
                <Input type="email" v-bind="componentField" :placeholder="$t('common.email')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <ClientOnly>
            <FormField v-slot="{ componentField, errors }" name="location_id">
              <FormItem>
                <FormLabel>{{ $t('common.location') }}*</FormLabel>
                <Select v-bind="componentField">
                  <SelectTrigger
                    :aria-label="$t('common.select_city')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('common.select_city')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="city in cities" :key="city.id" :value="city.id">{{
                      city.title
                    }}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <ClientOnly v-if="showBranchesField">
            <FormField v-slot="{ componentField, errors }" name="branch_id">
              <FormItem>
                <FormLabel>{{ $t('form.credit_request.fields.branch') }}*</FormLabel>
                <Select v-bind="componentField" :disabled="!hasBranchesInLocation">
                  <SelectTrigger
                    :aria-label="$t('form.credit_request.aria_labels.branch')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('form.credit_request.placeholders.branch')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="branch in filteredBranches"
                      :key="branch.id"
                      :value="branch.id"
                      >{{ branch.title }}</SelectItem
                    >
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <ClientOnly v-if="showCreditPurposesField">
            <FormField v-slot="{ componentField, errors }" name="credit_purpose_id">
              <FormItem>
                <FormLabel>{{ $t('form.credit_request.fields.purpose') }}</FormLabel>
                <Select v-bind="componentField">
                  <SelectTrigger
                    :aria-label="$t('form.credit_request.aria_labels.purpose')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('form.credit_request.placeholders.purpose')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="purpose in creditPurposes"
                      :key="purpose.id"
                      :value="purpose.id"
                      >{{ purpose.title }}</SelectItem
                    >
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <FormField v-slot="{ componentField, errors }" name="period">
            <FormItem>
              <FormLabel>{{ $t('common.term') }}*</FormLabel>
              <Select v-bind="componentField">
                <SelectTrigger
                  :aria-label="$t('form.credit_request.aria_labels.period')"
                  :aria-invalid="errors.length > 0"
                >
                  <SelectValue :placeholder="$t('form.credit_request.placeholders.period')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="period in periods" :key="period.value" :value="period.value">{{
                    period.title
                  }}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField, errors }" name="amount">
            <FormItem>
              <FormLabel>{{ $t('form.credit_request.fields.amount_currency') }}*</FormLabel>
              <FormControl>
                <div
                  :class="[
                    'flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border ring-offset-2',
                    errors.length
                      ? 'border-red-200 bg-red-50 focus-within:ring-2 focus-within:ring-red-500'
                      : 'border-gray-300 focus-within:ring-2 focus-within:ring-blue-600'
                  ]"
                >
                  <Input
                    type="number"
                    class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
                    min="1"
                    v-bind="componentField"
                    :placeholder="$t('form.credit_request.placeholders.amount')"
                  />

                  <FormField v-slot="{ componentField: currencyField }" name="currency_id">
                    <Select v-bind="currencyField" :disabled="currencies.length === 1">
                      <SelectTrigger
                        class="w-[100px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                        :aria-label="$t('common.currency')"
                      >
                        <SelectValue :placeholder="$t('common.choose_currency')" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="currency in currencies"
                          :key="currency.id"
                          :value="currency.id"
                        >
                          <div class="flex items-center gap-2">
                            <CurrencyIcon :code="currency.code" size="sm" country />
                            {{ currency.code }}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <ClientOnly v-if="showProductsField">
            <FormField v-slot="{ componentField, errors }" name="product_id">
              <FormItem>
                <FormLabel>{{ $t('form.credit_request.fields.product') }}*</FormLabel>
                <Select v-bind="componentField">
                  <SelectTrigger
                    :aria-label="$t('form.credit_request.aria_labels.product')"
                    :aria-invalid="errors.length > 0"
                  >
                    <SelectValue :placeholder="$t('form.credit_request.placeholders.product')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="product in products" :key="product.id" :value="product.id">
                      {{ product.title }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>
        </div>

        <FormField v-slot="{ componentField }" name="comments">
          <FormItem>
            <FormLabel>{{ $t('form.credit_request.fields.comments') }}</FormLabel>
            <FormControl>
              <Textarea
                v-bind="componentField"
                :placeholder="$t('form.credit_request.placeholders.comments')"
                rows="4"
                class="resize-none"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
          <FormItem>
            <FormControl>
              <div class="flex items-center space-x-2">
                <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
                <Label for="terms">
                  <FeatureCustomConsent v-if="customConsent" :consent="customConsent" />
                  <template v-else>
                    {{ $t('form.credit_request.agreement_prefix') }}
                    <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                      {{ $t('form.credit_request.agreement_link') }}
                    </NuxtLinkLocale>
                  </template>
                </Label>
              </div>
            </FormControl>
            <FormMessage v-if="errorMessage" :message="errorMessage" />
          </FormItem>
        </FormField>

        <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
          <Button type="submit" class="mt-4 md:mt-0">{{ $t('form.credit_request.submit') }}</Button>
        </div>
      </form>
    </div>
  </ContentBlock>
</template>
